import { CheckIcon, InformationCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import {
  ActionIcon,
  Alert,
  Autocomplete,
  Badge,
  Box,
  Button,
  Collapse,
  Group,
  List,
  rem,
  Stack,
  Text,
  Tooltip,
  useMantineColorScheme,
  useMantineTheme
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CVItemCard } from "../../../../../components/common/CVItemCard";
import { CVSectionContainer } from "../../../../../components/common/CVSectionContainer";
import { DeleteConfirmationModal } from "../../../../../components/common/DeleteConfirmationModal";
import { FormattableTextArea } from "../../../../../components/common/FormattableTextArea";
import { MonthYearInput } from "../../../../../components/common/MonthYearInput";
import {
  TechnologySelector,
  TechnologyWithFavorite,
} from "../../../../../components/common/TechnologySelector";
import { ValidationResult } from "../../../../../components/common/ValidationResult";
import { FormUIColor } from "../../../../../constants/colorPalette";
import { useEditMode } from "../../../../../context/EditModeContext";
import { useEmployersDictionary } from "../../../../../hooks/queries/useEmployersDictionary";
import { useRolesDictionary } from "../../../../../hooks/queries/useRolesDictionary";
import { useTechnologiesDictionary } from "../../../../../hooks/queries/useTechnologiesDictionary";
import { useColors } from "../../../../../hooks/useColors";
import {
  WorkExperience as WorkExperienceType,
} from "../../../../../types/cv";
import { ValidateableCvSections } from "../../../../../types/cvSections";
import {
  createDateString,
  formatDateRange,
  normalizeYearInput,
  parseDateString,
  toUTCDate,
} from "../../../../../utils/dateFormat";
import { WorkExperienceService } from "../services/workExperienceService";

// Isolated description field — owns its own state so typing never triggers a WorkExperience re-render
const DescriptionField = React.memo(function DescriptionField({
  initialValue,
  onCommit,
  ...rest
}: {
  initialValue: string;
  onCommit: (value: string) => void;
  label?: string;
  placeholder?: string;
  minRows?: number;
  maxRows?: number;
  enableImprovement?: boolean;
  improvePrompt?: string;
}) {
  const [value, setValue] = useState(initialValue);
  const [debouncedValue] = useDebouncedValue(value, 300);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    onCommit(debouncedValue);
  }, [debouncedValue]); // onCommit is intentionally omitted — parent must pass a stable useCallback

  return <FormattableTextArea value={value} onChange={setValue} {...rest} />;
});

const INSTRUCTIONS = (
  <Stack gap={5}>
    <Text fw={500}>Instructions from your company</Text>
    <Text size="sm">
      Provide all work experience you have (preferably including previous jobs
      that you may not necessarily find relevant – uncheck to exclude
      employments you don't want to be visible in the consultant profile but
      still searchable).
    </Text>
    <Text size="sm" mt={5}>
      - Start and end dates. For the Sopra Steria employment, leave the 'to'
      field blank.
    </Text>
    <Text size="sm">- Employer</Text>
    <Text size="sm">- Title</Text>
    <Text size="sm" mt={5}>
      Description of work experience: Write a brief description of the position,
      the main areas you worked in, and your responsibilities.
    </Text>
  </Stack>
);

// Local form state interfaces
interface WorkExperienceFormState {
  fromDate: string;
  toDate: string;
  employer: string;
  position: string;
  description: string;
  fromMonth: string;
  fromYear: string;
  toMonth: string;
  toYear: string;
  technologies: TechnologyWithFavorite[];
}

interface WorkExperienceEditState extends WorkExperienceFormState {
  id: string;
}

interface WorkExperienceProps {
  workExperiences: WorkExperienceType[];
  onChange: (workExperiences: WorkExperienceType[]) => void;
  userId?: number;
  cvId?: number;
}

export function WorkExperience({
  workExperiences,
  onChange,
  userId,
  cvId,
}: WorkExperienceProps) {
  const { isEditMode } = useEditMode();
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';
  const [isAddFormOpen, setIsAddFormOpen] = useState(false);
  const [editingExperienceId, setEditingExperienceId] = useState<string | null>(
    null
  );
  const colors = useColors();

  // Function to format work experience data for LLM validation
  const getWorkExperienceSectionData = (): string => {
    if (!workExperiences || workExperiences.length === 0) {
      return 'No work experience entries found.';
    }

    return workExperiences.map((work, index) => {
      const dateRange = formatDateRange(work.fromDate, work.toDate);
      return `
Work Experience ${index + 1}:
- Period: ${dateRange}
- Employer: ${work.employer}
- Position: ${work.position}
- Description: ${work.description || 'No description provided'}
- Technologies: ${work.technologies?.map(t => t.name).join(', ') || 'None specified'}
      `.trim();
    }).join('\n\n');
  };

  // Use React Query for all dictionaries — deduplicates across components, cached 15 min
  const { data: technologiesDictionary = [], isLoading: technologiesLoading } =
    useTechnologiesDictionary();
  const { data: employersDictionaryData, isLoading: employersLoading } = useEmployersDictionary();
  const { data: rolesDictionaryData, isLoading: rolesLoading } = useRolesDictionary();
  const employersDictionary = employersDictionaryData?.filter((e): e is string => !!e) ?? [];
  const rolesDictionary = rolesDictionaryData?.filter((r): r is string => !!r) ?? [];
  const isLoadingDictionaries = technologiesLoading || employersLoading || rolesLoading;

  const [newExperience, setNewExperience] = useState<WorkExperienceFormState>({
    fromDate: "",
    toDate: "",
    employer: "",
    position: "",
    description: "",
    fromMonth: "",
    fromYear: "",
    toMonth: "",
    toYear: "",
    technologies: [],
  });

  const [editExperience, setEditExperience] = useState<WorkExperienceEditState>(
    {
      id: "",
      fromDate: "",
      toDate: "",
      employer: "",
      position: "",
      description: "",
      fromMonth: "",
      fromYear: "",
      toMonth: "",
      toYear: "",
      technologies: [],
    }
  );

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [experienceToDelete, setExperienceToDelete] =
    useState<WorkExperienceType | null>(null);
  const [showAddValidation, setShowAddValidation] = useState(false);
  const [showEditValidation, setShowEditValidation] = useState(false);

  // Local position input state — decoupled from form object to avoid re-renders on every keystroke
  const [newPositionInput, setNewPositionInput] = useState('');
  const [editPositionInput, setEditPositionInput] = useState('');
  const [debouncedNewPosition] = useDebouncedValue(newPositionInput, 300);
  const [debouncedEditPosition] = useDebouncedValue(editPositionInput, 300);

  useEffect(() => {
    setNewExperience((prev) => ({ ...prev, position: debouncedNewPosition }));
  }, [debouncedNewPosition]);

  useEffect(() => {
    setEditExperience((prev) => ({ ...prev, position: debouncedEditPosition }));
  }, [debouncedEditPosition]);

  // Local description input state — debounced to avoid re-rendering the whole form on every keystroke
  const [addDescKey, setAddDescKey] = useState(0);
  const handleNewDescriptionCommit = useCallback((value: string) => {
    setNewExperience((prev) => ({ ...prev, description: value }));
  }, []);
  const handleEditDescriptionCommit = useCallback((value: string) => {
    setEditExperience((prev) => ({ ...prev, description: value }));
  }, []);

  // Services
  const workExperienceService = new WorkExperienceService();

  // Close and reset all forms when edit mode is disabled
  useEffect(() => {
    if (!isEditMode) {
      setIsAddFormOpen(false);
      setEditingExperienceId(null);
      setNewExperience({
        fromDate: "",
        toDate: "",
        employer: "",
        position: "",
        description: "",
        fromMonth: "",
        fromYear: "",
        toMonth: "",
        toYear: "",
        technologies: [],
      });
      setNewPositionInput('');
      setEditPositionInput('');
      setAddDescKey((k) => k + 1);
      setDeleteModalOpen(false);
      setShowAddValidation(false);
      setShowEditValidation(false);
    }
  }, [isEditMode]);

  const handleAddExperience = async () => {
    setShowAddValidation(true);
    if (
      !newExperience.employer ||
      !newExperience.position ||
      !newExperience.fromMonth ||
      !newExperience.fromYear ||
      !userId
    )
      return;

    try {
      const normalizedFromYear = normalizeYearInput(newExperience.fromYear);
      const normalizedToYear = newExperience.toYear
        ? normalizeYearInput(newExperience.toYear)
        : "";

      // Convert month/year to Date objects for backend (UTC to avoid timezone shift)
      const fromDate = toUTCDate(
        new Date(
          parseInt(normalizedFromYear),
          parseInt(newExperience.fromMonth) - 1,
          1
        )
      );
      const toDate =
        newExperience.toMonth && normalizedToYear
          ? toUTCDate(
            new Date(
              parseInt(normalizedToYear),
              parseInt(newExperience.toMonth) - 1,
              1
            )
          )
          : null;

      // Create PersonTechnologyDto objects from selected technologies
      const technologies = newExperience.technologies.map((tech) => ({
        technology: {
          id: tech.id,
          name: tech.name,
        },
        personId: userId,
        favourite: tech.isFavorite || false,
      }));

      // Create work experience data for backend
      const workExperienceData = {
        dateStart: fromDate,
        dateEnd: toDate,
        employer: newExperience.employer,
        position: newExperience.position,
        description: newExperience.description || "",
        personId: userId,
        technologies,
      };

      // Call backend service
      const savedExperience = await workExperienceService.addWorkExperience(
        workExperienceData
      );

      // Create local experience object for UI
      const experience: WorkExperienceType = {
        id: savedExperience.id.toString(),
        fromDate: createDateString(
          newExperience.fromMonth,
          normalizedFromYear
        ),
        toDate:
          newExperience.toMonth && normalizedToYear
            ? createDateString(newExperience.toMonth, normalizedToYear)
            : "",
        employer: newExperience.employer,
        position: newExperience.position,
        description: newExperience.description || "",
        technologies: newExperience.technologies.map((tech) => ({ name: tech.name, isFavorite: tech.isFavorite })),
      };

      // Update local state
      onChange([...workExperiences, experience]);

      // Reset form
      setNewExperience({
        fromDate: "",
        toDate: "",
        employer: "",
        position: "",
        description: "",
        fromMonth: "",
        fromYear: "",
        toMonth: "",
        toYear: "",
        technologies: [],
      });
      setNewPositionInput('');
      setIsAddFormOpen(false);
      setAddDescKey((k) => k + 1);
      setShowAddValidation(false);

      // Show success notification
      notifications.show({
        title: "Success",
        message: "Work experience added successfully",
        color: "green",
      });
    } catch (error) {
      console.error("Error adding work experience:", error);
      notifications.show({
        title: "Error",
        message: "Failed to add work experience. Please try again.",
        color: "red",
      });
    }
  };

  const handleRemoveExperience = async (id: string) => {
    try {
      // Call backend service to delete
      await workExperienceService.deleteWorkExperience(parseInt(id));

      // Update local state
      onChange(workExperiences.filter((exp) => exp.id !== id));

      // Show success notification
      notifications.show({
        title: "Success",
        message: "Work experience deleted successfully",
        color: "green",
      });
    } catch (error) {
      console.error("Error deleting work experience:", error);
      notifications.show({
        title: "Error",
        message: "Failed to delete work experience. Please try again.",
        color: "red",
      });
    }
  };

  const handleEditExperience = (exp: WorkExperienceType) => {
    const { month: fromMonth, year: fromYear } = parseDateString(exp.fromDate);
    const { month: toMonth, year: toYear } = exp.toDate
      ? parseDateString(exp.toDate)
      : { month: "", year: "" };

    // Convert technologies to TechnologyWithFavorite objects, preserving isFavorite from stored state
    const technologies: TechnologyWithFavorite[] = (exp.technologies || [])
      .map((techItem) => {
        const tech = technologiesDictionary.find((t) => t.name === techItem.name);
        return {
          id: tech ? tech.id : 0,
          name: techItem.name,
          isFavorite: techItem.isFavorite,
        };
      })
      .filter((tech) => tech.id !== 0);

    setEditingExperienceId(exp.id);
    setEditPositionInput(exp.position);
    setEditExperience({
      id: exp.id,
      fromDate: exp.fromDate,
      toDate: exp.toDate,
      employer: exp.employer,
      position: exp.position,
      description: exp.description,
      fromMonth,
      fromYear,
      toMonth,
      toYear,
      technologies,
    });
  };

  const saveExperienceEdit = async () => {
    setShowEditValidation(true);
    if (
      !editingExperienceId ||
      !editExperience.employer ||
      !editExperience.position ||
      !editExperience.fromMonth ||
      !editExperience.fromYear ||
      !userId
    )
      return;

    try {
      const normalizedFromYear = normalizeYearInput(editExperience.fromYear);
      const normalizedToYear = editExperience.toYear
        ? normalizeYearInput(editExperience.toYear)
        : "";

      // Convert month/year to Date objects for backend (UTC to avoid timezone shift)
      const fromDate = toUTCDate(
        new Date(
          parseInt(normalizedFromYear),
          parseInt(editExperience.fromMonth) - 1,
          1
        )
      );
      const toDate =
        editExperience.toMonth && normalizedToYear
          ? toUTCDate(
            new Date(
              parseInt(normalizedToYear),
              parseInt(editExperience.toMonth) - 1,
              1
            )
          )
          : null;

      // Create PersonTechnologyDto objects from selected technologies
      const technologies = editExperience.technologies.map((tech) => ({
        technology: {
          id: tech.id,
          name: tech.name,
        },
        personId: userId,
        favourite: tech.isFavorite || false,
      }));

      // Create work experience data for backend
      const workExperienceData = {
        dateStart: fromDate,
        dateEnd: toDate,
        employer: editExperience.employer,
        position: editExperience.position,
        description: editExperience.description || "",
        personId: userId,
        technologies,
      };

      // Call backend service
      const updatedBackendExperience =
        await workExperienceService.updateWorkExperience(
          parseInt(editingExperienceId),
          workExperienceData
        );

      // Create updated experience object for UI
      const updatedExperience: WorkExperienceType = {
        id: editingExperienceId,
        fromDate: createDateString(
          editExperience.fromMonth,
          normalizedFromYear
        ),
        toDate:
          editExperience.toMonth && normalizedToYear
            ? createDateString(editExperience.toMonth, normalizedToYear)
            : "",
        employer: editExperience.employer,
        position: editExperience.position,
        description: editExperience.description || "",
        technologies: editExperience.technologies.map((tech) => ({ name: tech.name, isFavorite: tech.isFavorite })),
      };

      // Update local state
      onChange(
        workExperiences.map((exp) =>
          exp.id === editingExperienceId ? updatedExperience : exp
        )
      );

      setEditingExperienceId(null);
      setEditPositionInput('');
      setShowEditValidation(false);

      // Show success notification
      notifications.show({
        title: "Success",
        message: "Work experience updated successfully",
        color: "green",
      });
    } catch (error) {
      console.error("Error updating work experience:", error);
      notifications.show({
        title: "Error",
        message: "Failed to update work experience. Please try again.",
        color: "red",
      });
    }
  };

  const openDeleteConfirmation = (experience: WorkExperienceType) => {
    setExperienceToDelete(experience);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (experienceToDelete) {
      await handleRemoveExperience(experienceToDelete.id);
      setDeleteModalOpen(false);
      setExperienceToDelete(null);
    }
  };

  // Create sorted experiences array
  const sortedExperiences = useMemo(() => [...workExperiences].sort((a, b) => {
    // Use the end date if available, otherwise use the start date
    const dateA = a.toDate && a.toDate !== "" ? a.toDate : a.fromDate;
    const dateB = b.toDate && b.toDate !== "" ? b.toDate : b.fromDate;

    // Empty toDate (current job) should be considered newest
    if (!a.toDate && b.toDate) return -1;
    if (a.toDate && !b.toDate) return 1;

    // Compare dates in reverse order (newest first)
    return dateB.localeCompare(dateA);
  }), [workExperiences]);

  // Prepare technology options for TechnologySelector
  const technologyOptions = useMemo(
    () => (technologiesDictionary || []).map((tech) => ({
      id: tech.id,
      value: tech.name,
      label: tech.name,
    })),
    [technologiesDictionary]
  );

  return (
    <CVSectionContainer
      sectionId="work-experience"
      title="Work Experience"
      buttonText={isEditMode ? "Add Experience" : undefined}
      onAddClick={isEditMode ? () => setIsAddFormOpen(true) : undefined}
      instructions={isEditMode ? INSTRUCTIONS : undefined}
      faqPath="work-vs-project-experience"
      personId={userId}
      cvId={cvId}
      getSectionData={getWorkExperienceSectionData}
    >
      {/* Add validation result component */}
      <ValidationResult section={ValidateableCvSections.WorkExperience} />

      {/* Add new experience form */}
      <Collapse in={isAddFormOpen}>
        <Box
          p="md"
          style={{
            border: `2px solid ${theme.colors.cyan[7]}`,
            borderRadius: theme.radius.sm,
            marginBottom: "16px",
            backgroundColor: isDark ? theme.colors.dark[6] : FormUIColor.ADD_FORM_BG,
          }}
        >
          <Stack gap="md">
            <Group grow>
              <MonthYearInput
                label="Start"
                monthValue={newExperience.fromMonth}
                yearValue={newExperience.fromYear}
                onMonthChange={(month) =>
                  setNewExperience((prev) => ({ ...prev, fromMonth: month }))
                }
                onYearChange={(year) =>
                  setNewExperience((prev) => ({ ...prev, fromYear: year }))
                }
                required
              />
              <MonthYearInput
                label="End (leave blank if current)"
                monthValue={newExperience.toMonth}
                yearValue={newExperience.toYear}
                onMonthChange={(month) =>
                  setNewExperience((prev) => ({ ...prev, toMonth: month }))
                }
                onYearChange={(year) =>
                  setNewExperience((prev) => ({ ...prev, toYear: year }))
                }
              />
            </Group>

            <Group grow>
              <Autocomplete
                label="Employer"
                placeholder="Enter employer name"
                value={newExperience.employer}
                onChange={(value) =>
                  setNewExperience((prev) => ({ ...prev, employer: value }))
                }
                data={employersDictionary}
                limit={10}
                required
                disabled={isLoadingDictionaries}
                rightSection={
                  <Tooltip label="Choose from existing employers or type a new one freely" position="top" multiline maw={220} withArrow>
                    <ActionIcon variant="subtle" color="gray" size="sm" tabIndex={-1}>
                      <InformationCircleIcon style={{ width: rem(16) }} />
                    </ActionIcon>
                  </Tooltip>
                }
              />
              <Autocomplete
                label="Position"
                placeholder="Enter position title"
                value={newPositionInput}
                onChange={setNewPositionInput}
                data={rolesDictionary}
                limit={10}
                required
                disabled={isLoadingDictionaries}
                rightSection={
                  <Tooltip label="Choose from existing position titles or type a new one freely" position="top" multiline maw={220} withArrow>
                    <ActionIcon variant="subtle" color="gray" size="sm" tabIndex={-1}>
                      <InformationCircleIcon style={{ width: rem(16) }} />
                    </ActionIcon>
                  </Tooltip>
                }
              />
            </Group>

            <TechnologySelector
              label="Skills & Technologies"
              labelTooltip="Add all relevant skills here: technologies, frameworks, tools, methodologies, soft skills, and other skills (e.g. ML, AI, Recruitment, AML, DevOps tools)."
              placeholder="Type to search and add technologies used in this role..."
              value={newExperience.technologies}
              onChange={(technologies) =>
                setNewExperience((prev) => ({ ...prev, technologies }))
              }
              availableTechnologies={technologyOptions}
              disabled={isLoadingDictionaries}
              loading={isLoadingDictionaries}
              enableSuggestions={true}
            />

            <DescriptionField
              key={addDescKey}
              initialValue=""
              onCommit={handleNewDescriptionCommit}
              label="Description"
              placeholder="Describe your responsibilities and achievements"
              minRows={5}
              maxRows={20}
              enableImprovement={true}
              improvePrompt="Please improve this description while maintaining its core facts and technical details:"
            />

            <Group justify="flex-end">
              {showAddValidation && (() => {
                const errors: string[] = [];
                if (!newExperience.fromMonth || !newExperience.fromYear) errors.push('Start month and year are required');
                if (!newExperience.employer) errors.push('Employer is required');
                if (!newExperience.position) errors.push('Position is required');
                if (!userId) errors.push('Session error: person ID is missing — please reload the page');
                return errors.length > 0 ? (
                  <Alert color="red" w="100%" py="xs">
                    <List size="sm" spacing={2}>
                      {errors.map((e) => <List.Item key={e}>{e}</List.Item>)}
                    </List>
                  </Alert>
                ) : null;
              })()}
              <Button
                variant="outline"
                leftSection={<XMarkIcon width={16} height={16} />}
                onClick={() => { setIsAddFormOpen(false); setShowAddValidation(false); }}
              >
                Cancel
              </Button>
              <Button
                leftSection={<CheckIcon width={16} height={16} />}
                onClick={handleAddExperience}
              >
                Add Experience
              </Button>
            </Group>
          </Stack>
        </Box>
      </Collapse>

      {/* Experience list */}
      <Stack gap="md">
        {sortedExperiences.map((experience) => (
          <React.Fragment key={experience.id}>
            {editingExperienceId === experience.id ? (
              // Show edit form instead of the card when editing
              <Box
                mb="md"
                p="md"
                style={{
                  border: `2px solid ${theme.colors.blue[5]}`,
                  borderRadius: theme.radius.sm,
                  backgroundColor: isDark ? theme.colors.dark[6] : theme.colors.blue[0],
                }}
              >
                <Text size="sm" c="blue" fw={500} mb="md">
                  Editing: {experience.position} at {experience.employer}
                </Text>
                <Stack gap="md">
                  <Group grow>
                    <MonthYearInput
                      label="Start"
                      monthValue={editExperience.fromMonth}
                      yearValue={editExperience.fromYear}
                      onMonthChange={(month) =>
                        setEditExperience((prev) => ({
                          ...prev,
                          fromMonth: month,
                        }))
                      }
                      onYearChange={(year) =>
                        setEditExperience((prev) => ({
                          ...prev,
                          fromYear: year,
                        }))
                      }
                      required
                    />
                    <MonthYearInput
                      label="End (leave blank if current)"
                      monthValue={editExperience.toMonth}
                      yearValue={editExperience.toYear}
                      onMonthChange={(month) =>
                        setEditExperience((prev) => ({
                          ...prev,
                          toMonth: month,
                        }))
                      }
                      onYearChange={(year) =>
                        setEditExperience((prev) => ({ ...prev, toYear: year }))
                      }
                    />
                  </Group>

                  <Group grow>
                    <Autocomplete
                      label="Employer"
                      placeholder="Enter employer name"
                      value={editExperience.employer}
                      onChange={(value) =>
                        setEditExperience((prev) => ({
                          ...prev,
                          employer: value,
                        }))
                      }
                      data={employersDictionary}
                      limit={10}
                      required
                      disabled={isLoadingDictionaries}
                      rightSection={
                        <Tooltip label="Choose from existing employers or type a new one freely" position="top" multiline maw={220} withArrow>
                          <ActionIcon variant="subtle" color="gray" size="sm" tabIndex={-1}>
                            <InformationCircleIcon style={{ width: rem(16) }} />
                          </ActionIcon>
                        </Tooltip>
                      }
                    />
                    <Autocomplete
                      label="Position"
                      placeholder="Enter position title"
                      value={editPositionInput}
                      onChange={setEditPositionInput}
                      data={rolesDictionary}
                      limit={10}
                      required
                      disabled={isLoadingDictionaries}
                      rightSection={
                        <Tooltip label="Choose from existing position titles or type a new one freely" position="top" multiline maw={220} withArrow>
                          <ActionIcon variant="subtle" color="gray" size="sm" tabIndex={-1}>
                            <InformationCircleIcon style={{ width: rem(16) }} />
                          </ActionIcon>
                        </Tooltip>
                      }
                    />
                  </Group>

                  <TechnologySelector
                    label="Skills & Technologies"
                    labelTooltip="Add all relevant skills here: technologies, frameworks, tools, methodologies, soft skills, and other skills (e.g. ML, AI, Recruitment, AML, DevOps tools)."
                    placeholder="Type to search and add technologies used in this role..."
                    value={editExperience.technologies}
                    onChange={(technologies) =>
                      setEditExperience((prev) => ({ ...prev, technologies }))
                    }
                    availableTechnologies={technologyOptions}
                    disabled={isLoadingDictionaries}
                    loading={isLoadingDictionaries}
                    enableSuggestions={true}
                  />

                  <DescriptionField
                    key={editingExperienceId ?? 'none'}
                    initialValue={editExperience.description ?? ''}
                    onCommit={handleEditDescriptionCommit}
                    label="Description"
                    placeholder="Describe your responsibilities and achievements"
                    minRows={5}
                    maxRows={20}
                    enableImprovement={true}
                    improvePrompt="Please improve this description while maintaining its core facts and technical details:"
                  />

                  <Group justify="flex-end" gap="sm">
                    {showEditValidation && (() => {
                      const errors: string[] = [];
                      if (!editExperience.fromMonth || !editExperience.fromYear) errors.push('Start month and year are required');
                      if (!editExperience.employer) errors.push('Employer is required');
                      if (!editExperience.position) errors.push('Position is required');
                      if (!userId) errors.push('Session error: person ID is missing — please reload the page');
                      return errors.length > 0 ? (
                        <Alert color="red" w="100%" py="xs">
                          <List size="sm" spacing={2}>
                            {errors.map((e) => <List.Item key={e}>{e}</List.Item>)}
                          </List>
                        </Alert>
                      ) : null;
                    })()}
                    <Button
                      variant="outline"
                      leftSection={<XMarkIcon width={16} height={16} />}
                      onClick={() => { setEditingExperienceId(null); setEditPositionInput(''); setShowEditValidation(false); }}
                    >
                      Cancel
                    </Button>
                    <Button
                      leftSection={<CheckIcon width={16} height={16} />}
                      onClick={saveExperienceEdit}
                    >
                      Save
                    </Button>
                  </Group>
                </Stack>
              </Box>
            ) : (
              // Show the normal card when not editing
              <CVItemCard
                title={experience.position}
                subtitle={experience.employer}
                dates={formatDateRange(experience.fromDate, experience.toDate)}
                description={
                  <Stack gap="xs">
                    {experience.description && (
                      <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
                        {experience.description}
                      </Text>
                    )}
                    {experience.technologies &&
                      experience.technologies.length > 0 && (
                        <Group gap="xs">
                          <Text size="xs" c="dimmed" fw={500}>
                            Skills & Technologies:
                          </Text>
                          {experience.technologies.map((tech, index) => (
                            <Badge
                              key={index}
                              size="sm"
                              color={tech.isFavorite ? colors.tech.FAVORITE_TECHNOLOGY : colors.tech.TECHNOLOGY}
                              variant={tech.isFavorite ? "filled" : "light"}
                            >
                              {tech.name}
                            </Badge>
                          ))}
                        </Group>
                      )}
                  </Stack>
                }
                onEdit={() => handleEditExperience(experience)}
                onDelete={() => openDeleteConfirmation(experience)}
                isEditMode={isEditMode}
              />
            )}
          </React.Fragment>
        ))}
      </Stack>

      {/* Delete confirmation modal */}
      <DeleteConfirmationModal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        itemName={`${experienceToDelete?.position} at ${experienceToDelete?.employer}`}
      />
    </CVSectionContainer>
  );
}
